import {
  add,
  Identify,
  identify as amplitudeIdentify,
  groupIdentify,
  init as initAmplitude,
  setGroup,
  setUserId,
  track as amplitudeTrack,
} from "@amplitude/analytics-browser"
import { plugin as engagementPlugin } from "@amplitude/engagement-browser"
import type { User } from "@auth/me.query"

// Public browser-SDK key — shipped in the client bundle by design.
const AMPLITUDE_API_KEY = "ef5eb76408d61f9133b62ec0f063dfb2"

// All Amplitude traffic (analytics events and Engagement) is routed through our
// proxy rather than hitting Amplitude directly.

// In development (the local dev server) we log every analytics operation to the
// console instead of sending it, so local work never pollutes the project.
const isDev = import.meta.env.DEV

type EventProperties = Record<string, unknown>

// In dev, every operation is logged with the exact payload it would otherwise
// send, so there is a single source of truth and nothing to keep in sync.
const debugLog = (operation: string, payload?: EventProperties): void => {
  // oxlint-disable-next-line eslint/no-console
  console.info(`[analytics] ${operation}`, payload ?? "")
}

// We compose the analytics + engagement packages directly rather than using
// @amplitude/unified, which also bundles Session Replay (rrweb) and Experiments
// that we don't use. Autocapture handles page views, sessions, form
// interactions and file downloads; custom value moments are sent explicitly via
// event(). Events route through our proxy.
const AMPLITUDE_OPTIONS = {
  serverZone: "EU",
  serverUrl: "https://dharma.puzzel.com",
  autocapture: {
    pageViews: true,
    sessions: true,
    formInteractions: true,
    fileDownloads: true,
  },
} as const

const init = (): void => {
  if (isDev) {
    debugLog("init", AMPLITUDE_OPTIONS)
    return
  }
  void initAmplitude(AMPLITUDE_API_KEY, AMPLITUDE_OPTIONS)
  // Engagement (Guides & Surveys) runs as a plugin on the analytics instance,
  // routed through the same proxy.
  void add(
    engagementPlugin({
      serverZone: "EU",
      serverUrl: "https://dharma.puzzel.com",
      cdnUrl: "https://dharma.puzzel.com/engagement-cdn",
      mediaUrl: "https://dharma.puzzel.com/engagement-media",
    }),
  )
}

// The readable org name available to every user is the current solution's name
// from the session (the same name shown in the org switcher). A distinct
// customer-entity name only exists on the systemadmin endpoint, so we use the
// solution name as the customer label until the backend exposes one.
const solutionNameOf = (user: User): string =>
  user.solutions.find((solution) => solution.uid === user.solutionUid)?.name ??
  user.solutionUid

// Identify the signed-in user and attach two grouping levels: the customer
// (account), keyed on its unique customer key, and the solution, keyed on its
// UID. Readable names are stored as group properties so reports show org names
// rather than opaque IDs. No personal data (names, emails) is sent.
const identify = (user: User): void => {
  const solutionName = solutionNameOf(user)
  const userId = user.puzzelUserId

  if (isDev) {
    // The full user (incl. personal fields) is logged locally only — it is
    // never part of what gets sent to Amplitude below.
    debugLog("identify", {
      userId,
      userProperties: {
        roles: user.roles,
        customerKey: user.solutionCustomerId,
        solutionUid: user.solutionUid,
        solutionName,
      },
      groups: {
        customer: {
          id: user.solutionCustomerId,
          properties: { customerKey: user.solutionCustomerId },
        },
        solution: {
          id: user.solutionUid,
          properties: { name: solutionName },
        },
      },
      user,
    })
    return
  }

  setUserId(userId)
  setGroup("customer", user.solutionCustomerId)
  groupIdentify(
    "customer",
    user.solutionCustomerId,
    new Identify().set("customerKey", user.solutionCustomerId),
  )

  setGroup("solution", user.solutionUid)
  groupIdentify(
    "solution",
    user.solutionUid,
    new Identify().set("name", solutionName),
  )

  const userIdentify = new Identify()
    .set("roles", user.roles)
    // .set("hasSuperpowers", user.hasSuperpowers)
    .set("customerKey", user.solutionCustomerId)
    .set("solutionUid", user.solutionUid)
    .set("solutionName", solutionName)

  amplitudeIdentify(userIdentify)
}

const event = (name: string, properties?: EventProperties): void => {
  if (isDev) {
    debugLog(name, properties)
    return
  }
  void amplitudeTrack(name, properties)
}

export const analytics = { init, identify, event }
